import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-slate-600 text-sm sm:text-base leading-relaxed">{children}</div>
  </section>
);

const LegalLayout = ({ children }) => (
  <div className="min-h-screen bg-slate-50" data-testid="legal-page">
    <header className="border-b border-slate-200 bg-white">
      <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="legal-home-link">
          <img src="/logo.png" alt="OwlWorkLabs logo" className="w-9 h-9 object-contain" />
          <span className="text-lg font-bold text-slate-900">OwlWorkLabs</span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">{children}</main>
    <footer className="border-t border-slate-200 bg-white">
      <div className="max-w-3xl mx-auto px-6 py-8 text-center text-slate-500 text-xs">
        <p>© {new Date().getFullYear()} OwlWorkLabs Ltd. All rights reserved. · Company No. 16713122</p>
        <p className="mt-2">
          <Link to="/privacy" className="hover:text-slate-800 transition-colors">Privacy Policy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="hover:text-slate-800 transition-colors">Terms &amp; Conditions</Link>
        </p>
      </div>
    </footer>
  </div>
);

const PrivacyPolicy = () => {
  return (
    <LegalLayout>
      <SEO
        title="Privacy Policy | OwlWorkLabs"
        description="How OwlWorkLabs Ltd collects, uses, and protects your personal data, including waitlist sign-ups and analytics, in line with UK GDPR."
        canonical="https://www.owlworklabs.com/privacy"
      />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Legal</p>
      <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Privacy Policy</h1>
      <p className="text-sm text-slate-500 mt-3">Last updated: June 2026</p>

      <div className="mt-6 space-y-3 text-slate-600 text-sm sm:text-base leading-relaxed">
        <p>
          This Privacy Policy explains how OwlWorkLabs Ltd ("OwlWorkLabs", "we", "us") collects and uses your
          personal data when you use our website. We are committed to protecting your privacy and handling your
          data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection
          Act 2018.
        </p>
      </div>

      <Section title="Who we are">
        <p>
          OwlWorkLabs Ltd is a UK-registered venture studio based in London, United Kingdom. For any
          privacy-related questions or requests, contact us at{' '}
          <a href="mailto:owlworklabs@gmail.com" className="text-slate-900 underline">owlworklabs@gmail.com</a>.
        </p>
        {/* TODO: add UK company registration number and registered office address when available */}
      </Section>

      <Section title="Information we collect">
        <p>We only collect the information you choose to provide, plus limited technical data:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Waitlist sign-ups (JADE):</strong> your name, email address, and gender selection.</li>
          <li><strong>Contact:</strong> if you email us, we receive the details you include in that message.</li>
          <li><strong>Usage &amp; device data:</strong> collected via optional analytics only after you consent (see below).</li>
        </ul>
      </Section>

      <Section title="How we use your information">
        <ul className="list-disc pl-5 space-y-1">
          <li>To add you to the JADE waitlist and notify you about launches and early access.</li>
          <li>To respond to enquiries you send us.</li>
          <li>To understand and improve how our website is used (only with your consent).</li>
        </ul>
        <p>
          Our lawful bases are your consent (for waitlist sign-up and analytics) and our legitimate interests
          in operating and improving our website.
        </p>
      </Section>

      <Section title="Cookies and analytics">
        <p>
          We use strictly necessary functionality to run the site. We also use optional analytics —
          Meta Pixel and PostHog — to understand website usage. These optional tools are only loaded after you
          give consent via our cookie banner. You can withdraw consent at any time by clearing your browser
          storage for this site, after which optional analytics will not load.
        </p>
      </Section>

      <Section title="Sharing your information">
        <p>
          We do not sell your personal data. We share it only with service providers that help us operate the
          site (for example, our analytics providers where you have consented), and where required by law.
        </p>
      </Section>

      <Section title="Data retention">
        <p>
          We keep waitlist data for as long as necessary to contact you about the product, or until you ask us
          to remove it. You can request deletion at any time by emailing us.
        </p>
      </Section>

      <Section title="Your rights">
        <p>Under UK GDPR you have the right to access, correct, delete, or restrict the processing of your personal data, and to withdraw consent. To exercise any of these rights, email{' '}
          <a href="mailto:owlworklabs@gmail.com" className="text-slate-900 underline">owlworklabs@gmail.com</a>.
          You also have the right to complain to the UK Information Commissioner's Office (ICO).
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>We may update this policy from time to time. The "last updated" date above reflects the latest revision.</p>
      </Section>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
